import React, { useEffect, useState } from 'react';
import { allOffices, nearestOffice, distanceKm } from '../../lib/offices';
import { detectGeo } from '../../lib/api';
import { useI18n } from '../../i18n';
import { MapPin, Navigation, LocateFixed, Loader2 } from 'lucide-react';

const fmtKm = (km) => (km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`);

const ContactMap = () => {
  const { t } = useI18n();
  const [active, setActive] = useState(allOffices[0]);
  const [userPos, setUserPos] = useState(null); // {lat, lng, city, precise}
  const [nearest, setNearest] = useState(null); // office
  const [locating, setLocating] = useState(false);

  const applyPosition = (pos) => {
    const { office } = nearestOffice(pos.lat, pos.lng);
    if (office) {
      setUserPos(pos);
      setNearest(office);
      setActive(office);
    }
  };

  useEffect(() => {
    let mounted = true;
    detectGeo().then((pos) => { if (mounted && pos) applyPosition(pos); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usePreciseLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { applyPosition({ lat: p.coords.latitude, lng: p.coords.longitude, city: '', precise: true }); setLocating(false); },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  const kmTo = (o) => (userPos ? distanceKm(userPos.lat, userPos.lng, o.lat, o.lng) : null);

  const embedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(active.address)}&z=15&output=embed`;
  const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(active.address)}`;

  return (
    <section id="locations" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-orange-500">{t('contactMap.kicker')}</span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-slate-900">{t('contactMap.title')}</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">{t('contactMap.body')}</p>
          </div>
          <button
            onClick={usePreciseLocation}
            disabled={locating}
            data-testid="use-my-location-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white hover:border-orange-400 hover:text-orange-600 text-slate-700 text-xs font-bold px-4 py-2 transition-colors"
          >
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}
            {t('contactMap.useMyLocation')}
          </button>
        </div>

        {nearest && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-1.5" data-testid="nearest-office-banner">
            <LocateFixed className="h-3.5 w-3.5" />
            {t('contactMap.nearest')}: {nearest.label}
            {userPos && <span className="text-emerald-600/80">· ~{fmtKm(distanceKm(userPos.lat, userPos.lng, nearest.lat, nearest.lng))}</span>}
            {userPos?.city && !userPos.precise && <span className="text-emerald-600/60">({userPos.city})</span>}
          </div>
        )}

        <div className="mt-6 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="office-map-list">
            {allOffices.map((o) => {
              const isActive = o.address === active.address;
              const isNearest = nearest && o.address === nearest.address;
              const km = kmTo(o);
              return (
                <button
                  key={o.address}
                  onClick={() => setActive(o)}
                  data-testid={`office-map-item-${o.label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    isActive
                      ? 'border-orange-500 bg-white shadow-md ring-1 ring-orange-500/30'
                      : 'border-slate-200 bg-white/70 hover:border-orange-300 hover:bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <MapPin className={`h-4 w-4 shrink-0 ${isActive ? 'text-orange-500' : 'text-slate-400'}`} />
                    <span className="font-bold text-sm text-slate-900">{o.label}</span>
                    {isNearest && (
                      <span data-testid="nearest-office-badge" className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        {t('contactMap.nearest')}
                      </span>
                    )}
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-slate-400 border rounded-full px-2 py-0.5">{o.country}</span>
                  </div>
                  <div className="mt-1.5 pl-6 text-xs text-slate-500 leading-relaxed">
                    {o.address}
                    {km != null && <span className="ml-1.5 text-slate-400 font-semibold whitespace-nowrap">· {fmtKm(km)}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
              <iframe
                title={`Map — ${active.label}`}
                src={embedUrl}
                data-testid="office-map-iframe"
                className="w-full h-[400px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t bg-white">
                <div className="text-sm min-w-0">
                  <div className="font-bold text-slate-900">{active.label}</div>
                  <div className="text-xs text-slate-500 truncate">{active.address}</div>
                </div>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="office-map-directions"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-4 py-2 transition-colors"
                >
                  <Navigation className="h-3.5 w-3.5" /> {t('contactMap.directions')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMap;
